import { z } from 'zod';
import { defineFeature } from '../core/defineFeature.js';
import { FeatureType } from '../core/types.js';
const HcpProfileFunctionality = { id: 2, name: 'HCP Profile Functionality' };
export const hcpProfiles = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_profiles',
    uiName: 'hcpProfiles',
    displayName: 'HCP Profiles',
    description: 'Enable HCP profile functionality',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const hcpSchedulingAssistant = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_scheduling_assistant',
    uiName: 'hcpSchedulingAssistant',
    displayName: 'HCP Scheduling Assistant',
    description: 'Enable HCP scheduling assistant',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const predictiveScheduling = defineFeature({
    type: FeatureType.Boolean,
    propName: 'predictive_scheduling',
    uiName: 'predictiveScheduling',
    displayName: 'Predictive Scheduling',
    description: 'Enable predictive scheduling',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const smartRouting = defineFeature({
    type: FeatureType.Boolean,
    propName: 'smart_routing',
    uiName: 'smartRouting',
    displayName: 'Smart Routing',
    description: 'Enable smart routing for HCP interactions',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const hcpLogInteraction = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_log_interaction',
    uiName: 'hcpLogInteraction',
    displayName: 'HCP Log Interaction',
    description: 'Log HCP interactions for analytics',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const engagementResponseLabels = defineFeature({
    type: FeatureType.Boolean,
    propName: 'engagement_response_labels',
    uiName: 'engagementResponseLabels',
    displayName: 'Engagement Response Labels',
    description: 'Enable engagement response labels',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const engagementResponseTags = defineFeature({
    type: FeatureType.Boolean,
    propName: 'engagement_response_tags',
    uiName: 'engagementResponseTags',
    displayName: 'Engagement Response Tags',
    description: 'Enable engagement response tags',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const engagementFilter = defineFeature({
    type: FeatureType.Boolean,
    propName: 'engagement_filter',
    uiName: 'engagementFilter',
    displayName: 'Engagement Filter',
    description: 'Enable engagement filter',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatAggregationVisualization = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_aggregation_visualization',
    uiName: 'chatAggregationVisualization',
    displayName: 'Chat Aggregation Visualization',
    description: 'Enable visualization of aggregated chat data',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const hcpOpensearchIndex = defineFeature({
    type: FeatureType.Json,
    propName: 'hcp_opensearch_index',
    uiName: 'hcpOpensearchIndex',
    displayName: 'HCP OpenSearch',
    description: 'HCP OpenSearch connection and index (used in smart routing and HCP autocompletes)',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'json-string',
    schema: z.object({
        connectionIdentifier: z.string().nullable(),
        searchIndex: z.string(),
    }),
    default: {
        connectionIdentifier: null,
        searchIndex: 'key-opinion-leaders',
    },
    isSystem: true,
    editor: 'hcp-opensearch-config',
});
export const keyDiscussionQuestionsDisclaimer = defineFeature({
    type: FeatureType.String,
    propName: 'key_discussion_questions_disclaimer',
    uiName: 'keyDiscussionQuestionsDisclaimer',
    displayName: 'Key Discussion Questions Disclaimer',
    description: 'A disclaimer to appear alongside AI generated Key Discussion Questions',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: '',
    isSystem: true,
    flatModelSchema: z.string().nullable(),
});
export const hcpContactInformation = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_contact_information',
    uiName: 'hcpContactInformation',
    displayName: 'HCP Contact Information',
    description: 'Show HCP Contact Information in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpInteractionsSummary = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_interactions_summary',
    uiName: 'hcpInteractionsSummary',
    displayName: 'HCP Interactions Summary',
    description: 'Show HCP Interactions Summary in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpKeyDiscussionQuestions = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_key_discussion_questions',
    uiName: 'hcpKeyDiscussionQuestions',
    displayName: 'HCP Key Discussion Questions',
    description: 'Show Key Discussion Questions in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpKeyInsights = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_key_insights',
    uiName: 'hcpKeyInsights',
    displayName: 'HCP Key Insights',
    description: 'Show Key Insights in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpLeaderTopics = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_leader_topics',
    uiName: 'hcpLeaderTopics',
    displayName: 'HCP Leader Topics',
    description: 'Show Leader Topics in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpClinicalTrials = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_clinical_trials',
    uiName: 'hcpClinicalTrials',
    displayName: 'HCP Clinical Trials',
    description: 'Show Clinical Trials in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpPublications = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_publications',
    uiName: 'hcpPublications',
    displayName: 'HCP Publications',
    description: 'Show Publications in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const hcpConferencePresence = defineFeature({
    type: FeatureType.Boolean,
    propName: 'hcp_conference_presence',
    uiName: 'hcpConferencePresence',
    displayName: 'HCP Conference Presence',
    description: 'Show Conference Presence in the chat',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const fieldCoachingReport = defineFeature({
    type: FeatureType.Boolean,
    propName: 'field_coaching_report',
    uiName: 'fieldCoachingReport',
    displayName: 'Field Coaching Report',
    description: 'Generate a field coaching report after an HCP interaction',
    category: HcpProfileFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const hcpProfileFunctionalityFeatures = [
    hcpProfiles,
    hcpSchedulingAssistant,
    predictiveScheduling,
    smartRouting,
    hcpLogInteraction,
    engagementResponseLabels,
    engagementResponseTags,
    engagementFilter,
    chatAggregationVisualization,
    hcpOpensearchIndex,
    keyDiscussionQuestionsDisclaimer,
    hcpContactInformation,
    hcpInteractionsSummary,
    hcpKeyDiscussionQuestions,
    hcpKeyInsights,
    hcpLeaderTopics,
    hcpClinicalTrials,
    hcpPublications,
    hcpConferencePresence,
    fieldCoachingReport,
];
